import { motion } from "framer-motion";
import { Crown, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Slim banner shown at the top of the calendar while the user is on their free
 * trial, or in a warmer state after it expires. Falls back to nothing when
 * they're a paying Pro user or trial hasn't started.
 */
export default function TrialBanner({ onUpgrade }) {
  const { user } = useAuth();
  const pro = user?.pro;
  if (!pro) return null;
  if (pro.source === "paid") return null;

  const active = pro.source === "trial";
  const days = pro.trial_days_left ?? 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
      data-testid="trial-banner"
      className={`mb-4 rounded-2xl border p-3 md:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${
        active
          ? "bg-gradient-to-r from-kawaii-lavender/25 via-kawaii-pink/15 to-kawaii-peach/25 border-kawaii-lavender/40"
          : "bg-white border-kawaii-border"
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-2xl flex items-center justify-center flex-shrink-0 ${
          active ? "bg-white/80" : "bg-kawaii-border/60"
        }`}>
          {active ? (
            <Sparkles className="w-4 h-4 text-kawaii-lavender" strokeWidth={2.5} />
          ) : (
            <Crown className="w-4 h-4 text-kawaii-textSoft" strokeWidth={2.5} />
          )}
        </div>
        <div className="min-w-0">
          {active ? (
            <>
              <p className="text-sm font-body font-semibold text-kawaii-text">
                You're on your Pro free trial — {days} {days === 1 ? "day" : "days"} left ♡
              </p>
              <p className="text-[11px] font-body text-kawaii-textSoft mt-0.5">
                Unlimited chat, photos, PDF exports, and full Emergency access. No card required.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-body font-semibold text-kawaii-text">
                Your Pro trial has ended
              </p>
              <p className="text-[11px] font-body text-kawaii-textSoft mt-0.5">
                Keep Emergency, unlimited chat and photos — from £3.99/month.
              </p>
            </>
          )}
        </div>
      </div>
      <button
        onClick={onUpgrade}
        data-testid="trial-banner-cta"
        className={`kawaii-btn text-xs px-3.5 py-2 rounded-full font-heading font-semibold whitespace-nowrap inline-flex items-center gap-1.5 ${
          active
            ? "bg-white text-kawaii-text border border-kawaii-lavender/40"
            : "bg-gradient-to-r from-kawaii-pink to-kawaii-peach text-white shadow-sm"
        }`}
      >
        <Crown className="w-3.5 h-3.5" strokeWidth={2.5} />
        {active ? "Keep Pro after trial" : "Upgrade to Pro"}
      </button>
    </motion.div>
  );
}
